import {
  useGetSalaryGradeQuery,
  useGetSalaryStructureQuery,
  useUpdateSalaryGradeMutation,
  type SalaryGradeData,
} from '@/app/features/employee/emp-payroll';
import WithLoader from '@/components/WithLoader';
import useAuth from '@/hooks/useAuth';
import useDiscloser from '@/hooks/useDiscloser';
import { CirclePlusIcon, TrashIcon } from '@/icons';
import { cn } from '@/lib/cn';
import { formatDate } from '@/utils';
import { formatToSnakeCase, snakeToCamel } from '@/utils/string.utils';
import { Button, DatePicker, Form, Input, type FormInstance } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { Fragment } from 'react';
import CalendarIcon from '../../../Dashboard/_components/Summery/_icons/CalendarIcon';
import ManageBenefit from './ManageBenefit';
import PanelCard from './PanelCard';
import {
  CONTROL,
  DATE_DISPLAY,
  disabledBeforeSaved,
  GRADE_LIST,
  type ConfigPanelProps,
} from './salary-grade.types';
import { useConfigPanel } from './useConfigPanel';

type BenefitRow = {
  benefitName?: string;
  isTaxApplicable?: boolean;
  fixedAmount?: number | string | null;
  percentage?: number | string | null;
  percentageOf?: string | null;
  /** Set by the Flat / % radio inside the benefits drawer. */
  type?: 'flat' | 'percentage';
  /** False once removed in the drawer; those rows are dropped on save. */
  status?: boolean;
};

type GradeRow = {
  gradeName?: string;
  minSalary?: number | string;
  maxSalary?: number | string;
  fixedAmount?: number | string;
  benefits?: BenefitRow[];
};

type FormValues = { [GRADE_LIST]: GradeRow[]; effectiveDate: Dayjs };

/** Grade names are stored as identifiers, so only letters and digits survive. */
const GRADE_NAME_PATTERN = /^[a-zA-Z0-9]+$/;
const stripGradeName = (value: string) => value.replace(/[^a-zA-Z0-9]/g, '');

const toValues = (data: SalaryGradeData): FormValues => ({
  [GRADE_LIST]:
    data.grades?.map((grade) => ({
      gradeName: snakeToCamel(grade?.grade_name),
      minSalary: grade?.min_salary_bdt,
      maxSalary: grade?.max_salary_bdt,
      fixedAmount: grade?.daily_rate_bdt ?? '',
      benefits:
        grade?.benefits?.map((benefit) => ({
          benefitName: snakeToCamel(benefit?.benefit_name),
          isTaxApplicable: benefit?.is_tax_applicable,
          fixedAmount: Number(benefit?.fixed_amount_bdt || 0),
          percentageOf: benefit?.percentage_of,
          percentage: benefit?.percentage,
          type: benefit?.percentage ? ('percentage' as const) : ('flat' as const),
          status: true,
        })) || [],
    })) || [],
  effectiveDate: dayjs(data.grade_effective_date),
});

const validateGrades = (values: FormValues) =>
  values[GRADE_LIST]?.length ? null : 'add at least one grade';

/**
 * Field messages stay to a word or two: they render inside a narrow column.
 */
const amountValidator = (min: number) => (_: unknown, value: unknown) => {
  if (value && isNaN(Number(value))) return Promise.reject(new Error('Invalid'));
  if (value && Number(value) < min) {
    return Promise.reject(new Error(`Min ${min}`));
  }
  return Promise.resolve();
};

/** Min has to stay under max, so each side is checked against the other. */
const rangeValidator =
  (form: FormInstance, index: number, opposite: 'minSalary' | 'maxSalary') =>
  (_: unknown, value: unknown) => {
    const other = form.getFieldValue([GRADE_LIST, index, opposite]);
    if (!value || !other || isNaN(Number(other))) return Promise.resolve();

    const isMinField = opposite === 'maxSalary';
    const invalid = isMinField
      ? Number(value) >= Number(other)
      : Number(value) <= Number(other);

    return invalid
      ? Promise.reject(new Error(isMinField ? 'Below max' : 'Above min'))
      : Promise.resolve();
  };

/** One money cell of a grade row. `opposite` turns it into half of a range. */
const AmountField = ({
  index,
  field,
  min,
  opposite,
}: {
  index: number;
  field: 'minSalary' | 'maxSalary' | 'fixedAmount';
  min: number;
  opposite?: 'minSalary' | 'maxSalary';
}) => {
  const form = Form.useFormInstance();

  return (
    <Form.Item
      name={[index, field]}
      className="disabled-bg-slate-900 !mb-0"
      dependencies={opposite ? [[GRADE_LIST, index, opposite]] : undefined}
      rules={[
        { required: true, message: 'Required' },
        { validator: amountValidator(min) },
        ...(opposite ? [{ validator: rangeValidator(form, index, opposite) }] : []),
      ]}
    >
      <Input
        prefix="BDT"
        placeholder="00"
        className="!border-transparent"
        // Editing one end of the range re-checks the other.
        onChange={
          opposite
            ? () =>
                form.validateFields([
                  [GRADE_LIST, index, 'minSalary'],
                  [GRADE_LIST, index, 'maxSalary'],
                ])
            : undefined
        }
      />
    </Form.Item>
  );
};

const ManageGrade = ({ payType, isEditing, ref }: ConfigPanelProps) => {
  const [form] = Form.useForm<FormValues>();
  const { organizationId, profileInfo } = useAuth();
  const [benefitDrawer, openBenefits, closeBenefits] = useDiscloser();
  const isDaily = payType === 'DAILY';

  const { data, isLoading } = useGetSalaryGradeQuery(payType);
  const { data: structureData } = useGetSalaryStructureQuery(payType);
  const [updateSalaryGrade] = useUpdateSalaryGradeMutation();

  const grades = Form.useWatch(GRADE_LIST, form) || [];

  // Daily grades carry one flat day rate, monthly grades carry a range. The
  // last column is `auto` so the actions always sit flush against the edge.
  const rowGrid = cn(
    'grid gap-3 items-start',
    isDaily
      ? 'grid-cols-[minmax(90px,1fr)_minmax(110px,1fr)_auto]'
      : 'grid-cols-[minmax(90px,1fr)_minmax(100px,1fr)_minmax(100px,1fr)_auto]',
  );

  useConfigPanel(ref, {
    form,
    data: data?.data,
    toValues,
    isEditing,
    errorMessage: 'Grade update failed',
    extraValidation: validateGrades,
    submit: (values) =>
      updateSalaryGrade({
        organizationId,
        tenantId: profileInfo?.tenant_id,
        payBasis: payType,
        gradeEffectiveDate: formatDate(values.effectiveDate, 'YYYY-MM-DD'),
        grades: values[GRADE_LIST].map((grade) => ({
          gradeName: formatToSnakeCase(grade?.gradeName || ''),
          ...(isDaily
            ? { dailyRateBdt: Number(grade?.fixedAmount) || 0 }
            : {
                minSalary: Number(grade?.minSalary) || 0,
                maxSalary: Number(grade?.maxSalary) || 0,
              }),
          benefits: (grade?.benefits || [])
            .filter((benefit) => benefit?.status)
            .map((benefit) => ({
              benefitName: formatToSnakeCase(benefit?.benefitName || ''),
              isTaxApplicable: Boolean(benefit?.isTaxApplicable),
              ...(benefit?.fixedAmount
                ? { fixedAmount: Number(benefit.fixedAmount) }
                : {}),
              ...(benefit?.percentage && benefit?.percentageOf
                ? {
                    percentageOf: benefit.percentageOf,
                    percentage: Number(benefit.percentage),
                  }
                : {}),
            })),
        })),
      }).unwrap(),
  });

  return (
    <PanelCard title="Grade Management">
      <WithLoader isLoading={isLoading} className="rounded-lg">
        <Form
          form={form}
          size="middle"
          layout="vertical"
          disabled={!isEditing || isLoading}
          className="custom-form label-gap compact-error space-y-4"
        >
          {/* Header and rows share one grid, so the columns always line up */}
          <Form.List name={GRADE_LIST}>
            {(fields, { add, remove }) => (
              <div className="overflow-x-auto">
                <div className={cn(rowGrid, 'min-w-[420px]')}>
                  <span className="text-xs text-white/40">Grade</span>
                  {isDaily ? (
                    <span className="text-xs text-white/40">Daily Salary</span>
                  ) : (
                    <>
                      <span className="text-xs text-white/40">Monthly Min</span>
                      <span className="text-xs text-white/40">Monthly Max</span>
                    </>
                  )}
                  <span className="text-xs text-white/40 text-right">
                    Benefits
                  </span>

                  {fields.map(({ key, name }) => (
                    <Fragment key={key}>
                      <Form.Item
                        validateFirst
                        className="!mb-0"
                        name={[name, 'gradeName']}
                        rules={[
                          { required: true, message: 'Required' },
                          {
                            pattern: GRADE_NAME_PATTERN,
                            message: 'No symbols',
                          },
                        ]}
                      >
                        <Input
                          placeholder="GradeA"
                          className="!bg-transparent !border-theme-slate-700"
                          // Letters and digits only, enforced as it is typed.
                          onChange={(event) =>
                            form.setFieldValue(
                              [GRADE_LIST, name, 'gradeName'],
                              stripGradeName(event.target.value),
                            )
                          }
                        />
                      </Form.Item>

                      {isDaily ? (
                        <AmountField index={name} field="fixedAmount" min={1} />
                      ) : (
                        <>
                          <AmountField
                            index={name}
                            field="minSalary"
                            min={1}
                            opposite="maxSalary"
                          />
                          <AmountField
                            index={name}
                            field="maxSalary"
                            min={2}
                            opposite="minSalary"
                          />
                        </>
                      )}

                      <div className="flex items-center justify-end gap-2">
                        {/* Benefits open in both modes: read only while viewing */}
                        <button
                          type="button"
                          onClick={() =>
                            openBenefits({ type: 'ACTION', data: { name } })
                          }
                          className={cn(
                            CONTROL,
                            'px-3 bg-theme-slate-900 text-sm text-theme-primary whitespace-nowrap',
                          )}
                        >
                          {grades?.[name]?.benefits?.filter((b) => b?.status)
                            ?.length || 0}{' '}
                          Benefits
                        </button>
                        {isEditing && (
                          <Button
                            type="text"
                            onClick={() => remove(name)}
                            icon={<TrashIcon size={18} />}
                            classNames={{
                              icon: 'flex items-center justify-center',
                            }}
                            className={cn(
                              CONTROL,
                              '!w-[38px] !px-0 border border-theme-gray-800 !text-theme-error-500',
                            )}
                          />
                        )}
                      </div>
                    </Fragment>
                  ))}

                  {isEditing && (
                    <button
                      type="button"
                      onClick={() => add({ benefits: [] })}
                      className={cn(
                        CONTROL,
                        'col-span-full w-full border border-dashed border-theme-primary/60',
                        'flex items-center justify-center gap-2 text-sm text-theme-primary',
                        'hover:bg-theme-primary/10',
                      )}
                    >
                      <CirclePlusIcon size={18} />
                      Add grade
                    </button>
                  )}
                </div>
              </div>
            )}
          </Form.List>

          <Form.Item
            name="effectiveDate"
            label="Effective Date"
            className="disabled-bg-slate-900 !mb-0 max-w-[200px]"
            rules={[{ required: true, message: 'Select an effective date' }]}
          >
            <DatePicker
              format={DATE_DISPLAY}
              placeholder="Select Date"
              className="!border-transparent w-full"
              suffixIcon={<CalendarIcon className="text-lg text-white" />}
              disabledDate={disabledBeforeSaved(
                data?.data?.grade_effective_date,
              )}
            />
          </Form.Item>
        </Form>
      </WithLoader>

      <ManageBenefit
        form={form}
        open={benefitDrawer.open}
        readOnly={!isEditing}
        handleClose={closeBenefits}
        data={{
          name: benefitDrawer?.data?.name,
          gradeName: grades?.[benefitDrawer?.data?.name]?.gradeName,
          salaryStructures: structureData?.data?.salary_structures || [],
        }}
      />
    </PanelCard>
  );
};

export default ManageGrade;
